import React, { FC, useMemo } from 'react';
import { FaEdit } from 'react-icons/fa';
import { useQuery, useQueryClient } from 'react-query';
import IResponseData from 'types/api';
import API from 'core/Api';
import { ISelectOptionProps } from 'components/form/types';
import { useModal } from 'store/modal';
import { createErrorFromResponse } from 'core/Error';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import { CellProps, Column } from 'react-table';
import Button from 'components/Button';
import Badge from 'components/Badge';
import { useHistory } from 'react-router';
import TooltipWrapper from 'components/TooltipWrapper';
import { buildFilters, filters } from 'components/table/utils/filtersConfig';
import { formatDate } from 'utils/helpers/formatters';
import { useTranslation } from 'react-i18next';
import { IDocumentTemplate } from '../types';

export interface IProps {
  entityName: string;
  entityEndpoint: string;
}

const List: FC<IProps> = ({ entityName, entityEndpoint }) => {
  const { client } = useActiveClientContext();
  const fromOperator = client?.id;

  const templateType = fromOperator ? 'client-templates' : 'templates';

  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery<ISelectOptionProps[], Error>(
    [buildClientEntity('categories', client)],
    (): Promise<ISelectOptionProps[]> =>
      API.get<IResponseData<{ id: number; name: string }>>('categories').then(res =>
        res.data?.items
          .sort((a, b) => a.id - b.id)
          .map(cat => ({
            label: cat.name,
            value: cat.id,
          }))
      )
  );

  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const createTemplate = () => history.push(buildClientUrl('/document-templates/create', client));

  const { data: hasInactiveTemplates } = useQuery<boolean, Error>(
    [buildClientEntity('hasInactiveTemplates', client)],
    (): Promise<boolean> => API.get(`${templateType}/count-inactive`).then(res => Boolean(res.data?.totalCount))
  );

  const multipleActivate = () => {
    API.patch('client-templates/activate-all')
      .then(res => {
        showModal({
          type: 'success',
          content: `${res.data?.totalCount} шаблоина бяха активирани.`,
        });
        queryClient.invalidateQueries([buildClientEntity(entityName, client)]);
        queryClient.invalidateQueries([buildClientEntity('hasInactiveTemplates', client)]);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const handleStatusUpdate = (id: number) => {
    API.patch(`client-templates/${id}/toggle-active`)
      .then(() => {
        showModal({
          type: 'success',
          content: 'Шаблонът беше обновен.',
        });
        queryClient.invalidateQueries([buildClientEntity(entityName, client)]);
        queryClient.invalidateQueries([buildClientEntity('hasInactiveTemplates', client)]);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const columns: Array<Column<IDocumentTemplate>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Име',
      accessor: 'name',
    },
    {
      Header: 'Категория',
      accessor: 'category_id',
      disableSortBy: true,
      Cell: ({ cell }: CellProps<IDocumentTemplate>) => {
        if (categories) {
          const category = categories.find((cat: any) => cat.value === cell.row.original.category_id);
          return <>{category?.label || cell.row.original.category_id}</>;
        }
        return null;
      },
    },
    {
      Header: 'Статус',
      accessor: 'status',
      disableSortBy: true,
      Cell: ({ cell }: CellProps<IDocumentTemplate>) => {
        const { id, is_active: isActive } = cell.row.original;
        if (isActive && fromOperator)
          return (
            <TooltipWrapper
              id="tooltip-status-active"
              overlay="Промени статуса"
            >
              <Button
                ariaLabel="template status"
                btnType="primary"
                size="sm"
                onClick={() => handleStatusUpdate(id)}
              >
                Активен
              </Button>
            </TooltipWrapper>
          );

        if (!isActive && fromOperator)
          return (
            <TooltipWrapper
              id="tooltip-status-inactive"
              overlay="Промени статуса"
            >
              <Button
                ariaLabel="template status"
                btnType="danger"
                size="sm"
                onClick={() => handleStatusUpdate(id)}
              >
                Неактивен
              </Button>
            </TooltipWrapper>
          );

        if (isActive && !fromOperator)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: 'Дата на създаване',
      accessor: 'created_at',
      disableSortBy: true,
      Cell: ({ row }: CellProps<IDocumentTemplate>) => (
        <div>{formatDate(new Date(row.original.created_at), 'dd.mm.Y hh:ii:ss')}</div>
      ),
    },
    {
      Header: () => <div className="text-right">Действия</div>,
      accessor: 'actions',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      disableSortBy: true,
      Cell: ({ cell }: CellProps<IDocumentTemplate>) => {
        const { id, can_edit: canEdit } = cell.row.original;
        return (
          <div className="text-right">
            <TooltipWrapper
              id="tooltip-edit"
              overlay={canEdit ? 'Редактирай' : 'Глобалните шаблони не могат да бъдат редактирани'}
            >
              <Button
                btnType="primary"
                size="sm"
                outlined
                onClick={() => history.push(buildClientUrl(`/document-templates/edit/${id}`, client))}
                disabled={!canEdit}
              >
                <FaEdit size={14} />
              </Button>
            </TooltipWrapper>
          </div>
        );
      },
    },
  ];

  const tableFilters = useMemo(() => {
    const statusFilter = buildFilters({ ...filters.status, t, translatable: true });
    const categoryFilter = buildFilters({
      name: 'category_id',
      placeholder: 'Категория',
      options: categories,
    });

    return [statusFilter, categoryFilter];
  }, [categories]);

  return (
    <MainLayout
      title="Управление на шаблони за документи"
      error={errorCategories}
    >
      <FetchTable<IDocumentTemplate>
        loading={isLoadingCategories}
        entityName={buildClientEntity(entityName, client)}
        entityEndpoint={entityEndpoint}
        columns={columns}
        onAdd={createTemplate}
        addBtnLabel="Добавяне на шаблон"
        onMultipleActivation={multipleActivate}
        multipleActivateBtnLabel={fromOperator && hasInactiveTemplates ? 'Множествено активиране' : ''}
        pagination
        filters={tableFilters}
      />
    </MainLayout>
  );
};

export default List;
